import React from 'react'
import moment from 'moment'
import { Bar, Pie } from 'react-chartjs-2'
import filesize from 'filesize'
import { Card, CardBody, Col, FormGroup, Input, Label, Row } from 'reactstrap'
import SimpleLoader from '../SimpleLoader'
import ServiceKeyValueCard from './ServiceKeyValueCard'
import { TServiceUsageData } from '../../types/serviceView'

type TProps = {
    usage: TServiceUsageData | null
    loading: boolean
    error: string
    month: string
    year: string
    onMonthChange: (value: string) => void
    onYearChange: (value: string) => void
}

const monthOptions = moment.months().map((label, index) => ({
    label,
    value: String(index + 1).padStart(2, '0'),
}))

const yearOptions = Array.from({ length: 6 }, (_, index) =>
    String(moment().year() - index))

const getMetricValue = (
    usage: TServiceUsageData | null,
    id: string
): number =>
    Number(usage?.metrics.find((metric) => metric.id === id)?.value || 0)

const ServiceBroadbandPanel: React.FC<TProps> = ({
    usage,
    loading,
    error,
    month,
    year,
    onMonthChange,
    onYearChange,
}) => {
    const download = getMetricValue(usage, 'download')
    const upload = getMetricValue(usage, 'upload')
    const totalTransfer = download + upload

    const pieChart = {
        datasets: [
            {
                data: [download, upload],
                backgroundColor: ['#66E4FF', '#FFC599'],
            },
        ],
        labels: ['Download', 'Upload'],
    }

    const barChart = {
        labels: (usage?.days || []).map((item) => item.day),
        datasets: [
            {
                data: (usage?.days || []).map((item) => item.download),
                label: 'Download',
                backgroundColor: '#66E4FF',
            },
            {
                data: (usage?.days || []).map((item) => item.upload),
                label: 'Upload',
                backgroundColor: '#FFC599',
            },
        ],
    }

    return (
        <Card className="service-detail-card shadow-sm border-0 mt-4">
            <CardBody>
                <Row className="align-items-end mb-3">
                    <Col lg={6}>
                        <div className="service-detail-card__title mb-0">
                            Traffic report {month}/{year}
                        </div>
                    </Col>
                    <Col lg={3}>
                        <FormGroup className="mb-0">
                            <Label for="service-usage-month">Month</Label>
                            <Input
                                id="service-usage-month"
                                type="select"
                                value={month}
                                onChange={(event) =>
                                    onMonthChange(event.target.value)
                                }
                            >
                                {monthOptions.map((option) => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col lg={3}>
                        <FormGroup className="mb-0">
                            <Label for="service-usage-year">Year</Label>
                            <Input
                                id="service-usage-year"
                                type="select"
                                value={year}
                                onChange={(event) =>
                                    onYearChange(event.target.value)
                                }
                            >
                                {yearOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                </Row>
                <SimpleLoader loading={loading}>
                    {error ? (
                        <Card className="shadow-sm border-0">
                            <CardBody className="text-danger">{error}</CardBody>
                        </Card>
                    ) : null}
                    {!error && (!usage || usage.empty) ? (
                        <Card className="shadow-sm border-0">
                            <CardBody>
                                No usage available for selected period.
                            </CardBody>
                        </Card>
                    ) : null}
                    {!error && usage && !usage.empty ? (
                        <>
                            <Row>
                                <Col lg={5} className="mb-4">
                                    <ServiceKeyValueCard
                                        title="Current period"
                                        items={[
                                            {
                                                label: 'Download',
                                                value: filesize(download),
                                                tone: 'primary',
                                            },
                                            {
                                                label: 'Upload',
                                                value: filesize(upload),
                                                tone: 'warning',
                                            },
                                            {
                                                label: 'Total Transfer',
                                                value: filesize(totalTransfer),
                                            },
                                        ]}
                                    />
                                </Col>
                                <Col lg={7} className="mb-4">
                                    <div
                                        className="service-detail-chart service-detail-chart--pie"
                                        style={{ minHeight: '260px' }}
                                    >
                                        <Pie
                                            data={pieChart}
                                            options={{
                                                maintainAspectRatio: false,
                                                legend: {
                                                    display: true,
                                                    position: 'bottom',
                                                },
                                                tooltips: {
                                                    callbacks: {
                                                        label(
                                                            tooltipItems,
                                                            data
                                                        ) {
                                                            const label =
                                                                data.labels?.[
                                                                    tooltipItems.index
                                                                ] || ''
                                                            return `${label}: ${filesize(
                                                                data.datasets[
                                                                    tooltipItems.datasetIndex
                                                                ].data[
                                                                    tooltipItems.index
                                                                ]
                                                            )}`
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <div
                                className="service-detail-chart"
                                style={{ minHeight: '320px' }}
                            >
                                <Bar
                                    data={barChart}
                                    options={{
                                        maintainAspectRatio: false,
                                        legend: {
                                            display: true,
                                            position: 'bottom',
                                        },
                                        scales: {
                                            xAxes: [
                                                {
                                                    stacked: false,
                                                },
                                            ],
                                            yAxes: [
                                                {
                                                    stacked: false,
                                                    ticks: {
                                                        beginAtZero: true,
                                                        maxTicksLimit: 6,
                                                        callback(value) {
                                                            return filesize(
                                                                Number(value),
                                                                { base: 10 }
                                                            )
                                                        },
                                                    },
                                                },
                                            ],
                                        },
                                        tooltips: {
                                            callbacks: {
                                                label(tooltipItems) {
                                                    const label =
                                                        tooltipItems.datasetIndex ===
                                                        0
                                                            ? 'Download'
                                                            : 'Upload'
                                                    return `${label}: ${filesize(
                                                        Number(
                                                            tooltipItems.yLabel
                                                        )
                                                    )}`
                                                },
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </>
                    ) : null}
                </SimpleLoader>
            </CardBody>
        </Card>
    )
}

export default ServiceBroadbandPanel
